import { queryClient } from '@/app/providers/queryClient'
import { DraftFileField } from '@/features/analytics/invite-links/ui/Marking/components/Contract/DraftFileField/index'
import Icon from '@/features/analytics/invite-links/ui/Marking/icon'
import { useInviteLinksStore } from '@/features/analytics/model'
import { apiQueryKey } from '@/shared/api'
import { QuestionSVGIcon } from '@/shared/assets'
import { Checkbox, DisableTooltip, SelectDropdown } from '@/shared/ui'
import { Label } from '@/shared/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { DocumentRecord, ORDMarkingFormData } from '@synapex/prisma/client'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import { ContractUnion } from '@synapex/shared2/gateway-rest-api'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { typedMarkingVatOptions } from '@/features/analytics/invite-links/const/marking'
import CustomContractForm from './CustomContractForm'
import { FinalContractCard } from './FinalContractCard'
import { useContractComputed } from './useContractComputed'
import { useContractMutations } from './useContractMutations'
import { useContractValidation } from './useContractValidation'
import { useCustomContract } from './useCustomContract'
import { useVatInput } from './useVatInput'

type ContractProps = {
    markingFormData: ORDMarkingFormData
    openLinkedLinksModal: () => void
    isAgency: boolean
    workspaceHasInWorkAgencyContract: boolean
    contracts: ContractUnion[] | undefined
    linkedLinksCount: number
} & WorkspaceWithLinkDescriptor

export const Contract: React.FC<ContractProps> = ({
    markingFormData,
    openLinkedLinksModal,
    contracts,
    linkedLinksCount,
    isAgency,
    workspaceHasInWorkAgencyContract,
    ...props
}) => {
    const wwld = workspaceWithLinkMethods.pickWwld(props)

    const [error, setError] = useState<'edo' | 'ord' | false>(false)

    const { setContractError, contractError } = useInviteLinksStore()

    const validation = useContractValidation(markingFormData)
    const customContract = useCustomContract(contracts)
    const mutations = useContractMutations(markingFormData, wwld)
    const computed = useContractComputed(markingFormData)
    const vatInput = useVatInput(markingFormData, wwld, computed.canEditVat)

    async function handleSelectContract(contract: DocumentRecord) {
        if (!validation.validatePublisherAndPublishOption()) {
            return
        }

        vatInput.clearVatError()

        if (!markingFormData.ord_contragent_id) {
            return
        }

        void mutations.updateContract.mutateAsync({
            ...wwld,
            documentRecordId: contract.documentRecordId,
            ordContragentId: markingFormData.ord_contragent_id,
        })
    }

    async function handleSelectCustomContract(
        customContractId: number
    ): Promise<void> {
        if (!validation.validatePublisherAndPublishOption()) {
            return
        }

        vatInput.clearVatError()

        await mutations.updateMarkingFormData.mutateAsync({
            custom_contract_id: customContractId,
        })
    }

    function handleCreateContract(type: 'custom' | 'default') {
        if (!validation.validatePublisherAndPublishOption()) {
            return
        }

        vatInput.clearVatError()

        if (type === 'default') {
            if (!markingFormData.ord_contragent_id) {
                return
            }
            mutations.generateContract.mutate({
                ordContragentId: markingFormData.ord_contragent_id,
                ...wwld,
            })
        } else {
            customContract.openCreateForm()
        }
    }

    async function handleUploadContract(file: File) {
        if (!validation.validateOrdContragentId()) {
            return
        }

        if (!validation.validateDraftContract()) {
            return
        }

        if (
            !markingFormData.ord_contragent_id ||
            !markingFormData.draft_contract_id
        ) {
            return
        }

        mutations.uploadContractFile.mutate({
            file,
            ordContragentId: markingFormData.ord_contragent_id,
            draftContractId: markingFormData.draft_contract_id,
        })
    }

    async function handleDeleteContract(
        type: 'draft' | 'final',
        contract: DocumentRecord
    ): Promise<void> {
        if (type === 'draft') {
            if (!markingFormData.ord_contragent_id) {
                return
            }
            void mutations.deleteContract.mutateAsync({
                ...wwld,
                documentRecordId: contract.documentRecordId,
                ordContragentId: markingFormData.ord_contragent_id,
            })
        } else {
            void mutations.updateMarkingFormData.mutateAsync(
                {
                    final_contract_id: null,
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                if (!query.state.data) {
                                    return false
                                }
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                    markingFormData.draft_contract_id
                                )
                            },
                        })

                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.contracts(
                                wwld,
                                markingFormData.ord_contragent_id
                            ),
                        })
                    },
                }
            )
        }
    }

    async function handleUpdateDefaultContractNumber(
        documentRecordId: number,
        contractNumber: string
    ): Promise<void> {
        if (!validation.validateOrdContragentId()) {
            return
        }

        if (!markingFormData.ord_contragent_id) {
            return
        }

        await mutations.updateContractNumber.mutateAsync({
            ...wwld,
            documentRecordId,
            ordContragentId: markingFormData.ord_contragent_id,
            contractNumber,
        })
    }

    async function handleDeleteCustomContract(): Promise<void> {
        await mutations.updateMarkingFormData.mutateAsync(
            {
                custom_contract_id: null,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({
                        queryKey: apiQueryKey.contracts(
                            wwld,
                            markingFormData.ord_contragent_id
                        ),
                    })
                },
            }
        )
    }

    function handleChangeCheckboxStatus(type: 'edo' | 'ord') {
        if (type === 'edo' && computed.isBlockedByCreative) {
            return
        }

        if (type === 'edo' && !computed.hasDraftContract) {
            setError(type)
            return
        }

        if (
            type === 'ord' &&
            computed.isBlockedByCreative &&
            markingFormData.contract_flag
        ) {
            return
        }

        if (
            (type === 'ord' &&
                !computed.hasFinalContract &&
                !computed.hasEdoFlag) ||
            !computed.hasDraftContract
        ) {
            setError(type)
            return
        }

        if (type === 'edo') {
            mutations.updateMarkingFormData.mutate(
                {
                    contract_edo_flag: !markingFormData.contract_edo_flag,
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                if (!query.state.data) {
                                    return false
                                }
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                    site_link_id?: number
                                    invite_link_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                        markingFormData.draft_contract_id &&
                                    (markingFormData.site_link_id !==
                                        queryData.site_link_id ||
                                        markingFormData.invite_link_id !==
                                            queryData.invite_link_id)
                                )
                            },
                        })
                    },
                }
            )
        } else {
            mutations.updateMarkingFormData.mutate(
                {
                    contract_flag: !markingFormData.contract_flag,
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.contracts(
                                wwld,
                                markingFormData.ord_contragent_id
                            ),
                        })

                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                if (!query.state.data) {
                                    return false
                                }
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                    site_link_id?: number
                                    invite_link_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                        markingFormData.draft_contract_id &&
                                    (markingFormData.site_link_id !==
                                        queryData.site_link_id ||
                                        markingFormData.invite_link_id !==
                                            queryData.invite_link_id)
                                )
                            },
                        })
                    },
                }
            )
        }

        setContractError(false)
    }

    async function handleSubmitCustomContract(data: {
        number: string
        date: Date
    }) {
        if (!validation.validateOrdContragentId()) {
            return
        }

        const { number, date } = data

        const utcDate = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        )

        try {
            if (!markingFormData.ord_contragent_id) {
                return
            }

            if (
                customContract.customContractFormMode === 'edit' &&
                customContract.editingCustomContractId
            ) {
                await mutations.updateCustomContract.mutateAsync({
                    customContractId: customContract.editingCustomContractId,
                    number,
                    date: utcDate,
                    ad_distributor_contragent_id:
                        markingFormData.ord_contragent_id,
                })
                toast.success('Договор успешно обновлён')
            } else {
                await mutations.createCustomContract.mutateAsync({
                    number,
                    date: utcDate,
                    ad_distributor_contragent_id:
                        markingFormData.ord_contragent_id,
                })
                toast.success('Договор успешно создан')
            }
            customContract.handleCustomContractFormOpenChange(false)
        } catch (error) {
            if (isAxiosError(error)) {
                const data = error.response?.data as
                    | { message?: string }
                    | undefined
                if (data?.message) {
                    toast.error(data.message)
                } else {
                    toast.error(
                        customContract.customContractFormMode === 'edit'
                            ? 'Ошибка при обновлении договора'
                            : 'Ошибка при создании договора'
                    )
                }
            } else {
                toast.error(
                    customContract.customContractFormMode === 'edit'
                        ? 'Ошибка при обновлении договора'
                        : 'Ошибка при создании договора'
                )
            }
        }
    }

    const errorText =
        contractError && typeof contractError === 'string' ? contractError : ''

    return (
        <>
            <div className="relative flex flex-col gap-[6px]">
                <div className="flex w-[448px] justify-between">
                    <div className="flex items-center gap-[6px]">
                        <Icon
                            error={computed.contractFlagError}
                            icon="contract"
                            checked={markingFormData.contract_flag}
                        />

                        <h2 className="text-sm font-semibold">
                            Договор ({isAgency ? 'РА-РР' : 'РД-РР'})
                        </h2>

                        <Tooltip>
                            <TooltipTrigger>
                                <QuestionSVGIcon className="cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <p>Информация о договоре</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    {linkedLinksCount > 1 && (
                        <div className="flex items-center gap-[6px]">
                            <a
                                className="cursor-pointer text-xs text-black/40 underline"
                                onClick={openLinkedLinksModal}
                            >
                                Связанные элементы
                            </a>
                            <div className="relative flex h-4 w-4 items-center justify-center overflow-hidden rounded-[20px] bg-blue-primary text-[10px] text-white">
                                {linkedLinksCount}
                            </div>
                        </div>
                    )}
                </div>

                <div className="h-[28px]" />
                <div className="absolute top-3 flex w-[448px] items-end gap-2">
                    <h2 className="pb-1 text-sm font-semibold">
                        Тип договора: стандартный
                    </h2>

                    <div className="flex flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            НДС, %
                        </span>

                        <DisableTooltip
                            enabled={!computed.canEditVat}
                            message="Если хотите отредактировать, удалите договор"
                        >
                            <SelectDropdown
                                disabled={!computed.canEditVat}
                                options={typedMarkingVatOptions}
                                onSelect={(v) =>
                                    vatInput.onUpdateVatRate(v.value)
                                }
                                value={markingFormData.vat_percent.toString()}
                                className="w-[98px]"
                                headerClassName="px-[10px] py-2"
                                labelClassName="p-0 overflow-visible"
                                error={!!vatInput.vatError}
                            />
                        </DisableTooltip>
                    </div>
                </div>

                <div className="flex items-end justify-between">
                    <div className="flex w-[448px] items-start justify-between">
                        <div className="relative flex h-[68px] flex-col justify-end gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Договор
                            </span>
                            <div>
                                <DisableTooltip
                                    enabled={
                                        computed.shouldDisableAllFields ||
                                        computed.shouldDisableDraftField ||
                                        (isAgency &&
                                            !workspaceHasInWorkAgencyContract)
                                    }
                                    message={
                                        isAgency &&
                                        !workspaceHasInWorkAgencyContract
                                            ? 'Нельзя добавить документ для агентских договоров, так как нет агентских договоров в работе'
                                            : !computed.hasPublisherFlag
                                              ? 'Для создания договора нажмите чекбокс “Издатель добавлен“'
                                              : 'Если хотите отредактировать, то уберите галочку справа'
                                    }
                                >
                                    <DraftFileField
                                        onClickCreate={handleCreateContract}
                                        onClickEditCustomContract={
                                            customContract.handleEditCustomContract
                                        }
                                        markingFormData={markingFormData}
                                        onSelect={handleSelectContract}
                                        onSelectCustomContract={
                                            handleSelectCustomContract
                                        }
                                        onDeleteItem={handleDeleteContract}
                                        onUpdateDefaultContractNumber={
                                            handleUpdateDefaultContractNumber
                                        }
                                        onDeleteCustomContract={
                                            handleDeleteCustomContract
                                        }
                                        contracts={contracts}
                                        disabled={
                                            computed.shouldDisableAllFields ||
                                            computed.shouldDisableDraftField ||
                                            (isAgency &&
                                                !workspaceHasInWorkAgencyContract)
                                        }
                                        disabledControlButton={
                                            !computed.hasPublisherAndPublishOptionsflags ||
                                            (isAgency &&
                                                !workspaceHasInWorkAgencyContract)
                                        }
                                        error={
                                            Boolean(errorText) || Boolean(error)
                                        }
                                        createContractTooltip="Нельзя создать документ, если не введён НДС"
                                        isLoading={
                                            mutations.generateContract
                                                .isPending ||
                                            mutations.updateContract
                                                .isPending ||
                                            mutations.updateContractNumber
                                                .isPending ||
                                            mutations.deleteContract
                                                .isPending ||
                                            mutations.globalDeleteContractPending ||
                                            mutations.globalUpdateContractPending ||
                                            mutations.globalUpdateContractNumberPending ||
                                            mutations.globalGenerateContractPending ||
                                            mutations.createCustomContract
                                                .isPending ||
                                            mutations.updateCustomContract
                                                .isPending
                                        }
                                        message="Выберите или сгенерируйте договор"
                                        createNewDocumentMessage="Сгененировать договор"
                                        className="w-48"
                                    />
                                </DisableTooltip>
                            </div>
                        </div>

                        <div className="flex h-[68px] flex-col justify-end gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Подписанный договор
                            </span>

                            <DisableTooltip
                                enabled={!computed.canUploadFinalContract}
                                message={
                                    markingFormData.contract_edo_flag
                                        ? 'Договор уже подписан в ЭДО'
                                        : 'Если хотите отредактировать, то уберите галочку справа'
                                }
                            >
                                <FinalContractCard
                                    {...wwld}
                                    contracts={contracts}
                                    markingFormData={markingFormData}
                                    onFileChange={handleUploadContract}
                                    onDeleteContract={handleDeleteContract}
                                    disabled={!computed.canUploadFinalContract}
                                    error={error === 'ord'}
                                    isLoading={
                                        mutations.uploadContractFile
                                            .isPending ||
                                        mutations.updateMarkingFormData
                                            .isPending ||
                                        mutations.globalUpdateMarkingFormDataPending
                                    }
                                    className="w-48"
                                />
                            </DisableTooltip>
                        </div>

                        <div className="relative flex h-[68px] flex-col items-center justify-end gap-[6px]">
                            <span className="w-[60px] break-words text-center text-[10px] font-medium leading-3">
                                Отправить в ОРД
                            </span>
                            <DisableTooltip
                                enabled={!computed.canEnableEdoCheckbox}
                                message={
                                    computed.isBlockedByCreative
                                        ? 'Нельзя отжать так как креатив уже сформирован'
                                        : 'Пожалуйста, заполните все параметры, чтобы отметить этот пункт'
                                }
                            >
                                <div
                                    onClick={() => {
                                        if (!computed.canEnableEdoCheckbox) {
                                            handleChangeCheckboxStatus('edo')
                                        }
                                    }}
                                    className="flex h-8 items-center"
                                >
                                    <Checkbox
                                        checked={
                                            markingFormData.contract_edo_flag
                                        }
                                        disabled={
                                            !computed.canEnableEdoCheckbox
                                        }
                                        onChange={() =>
                                            handleChangeCheckboxStatus('edo')
                                        }
                                        sizeVariant="big"
                                        variant={
                                            contractError ? 'error' : 'green'
                                        }
                                        size={20}
                                    />
                                </div>
                            </DisableTooltip>
                        </div>
                    </div>

                    <div className="flex w-[105px] items-end gap-[6px] pb-[5px]">
                        <DisableTooltip
                            enabled={computed.isContractCheckboxDisabled}
                            className="h-5"
                            message={
                                computed.isBlockedByCreative &&
                                markingFormData.contract_flag
                                    ? 'Нельзя отжать так как креатив уже сформирован'
                                    : 'Пожалуйста, заполните все параметры, чтобы отметить этот пункт'
                            }
                        >
                            <div
                                onClick={() => {
                                    if (computed.isContractCheckboxDisabled) {
                                        handleChangeCheckboxStatus('ord')
                                    }
                                }}
                            >
                                <Checkbox
                                    id="contract_allow"
                                    checked={markingFormData.contract_flag}
                                    disabled={
                                        computed.isContractCheckboxDisabled
                                    }
                                    onChange={() =>
                                        handleChangeCheckboxStatus('ord')
                                    }
                                    sizeVariant="big"
                                    variant={contractError ? 'error' : 'green'}
                                    size={20}
                                />
                            </div>
                        </DisableTooltip>
                        <Label
                            htmlFor="contract_allow"
                            className="w-[79px] text-[10px] font-medium leading-3"
                        >
                            Договор подписан
                        </Label>
                    </div>
                </div>
            </div>
            <CustomContractForm
                open={customContract.customContractFormOpen}
                setOpen={customContract.handleCustomContractFormOpenChange}
                onSubmit={handleSubmitCustomContract}
                mode={customContract.customContractFormMode}
                initialValues={customContract.customContractInitialValues}
                isSubmitting={
                    mutations.createCustomContract.isPending ||
                    mutations.updateCustomContract.isPending
                }
            />
        </>
    )
}
